/**
 * Parts of one whole as a single bar, in the emphasis form: the first part (the one the story is about) in the
 * accent, the rest in greys, darkest first, with a 2px gap between segments. The legend under it names every part
 * with its value and share, so identity never rides colour alone; hovering a segment lifts its row. More than four
 * parts fold the smallest into "Other" rather than inventing a colour.
 */
import { useState } from 'react'
import { cx } from '../cx'
import s from './StackBar.module.css'

export interface StackPart {
  key: string
  label: string
  value: number
}

const TONES = ['accent', 'rest1', 'rest2', 'rest3'] as const

export function StackBar({ label, parts, format }: { label: string; parts: StackPart[]; format: (v: number) => string }) {
  const [hover, setHover] = useState<string | null>(null)
  const shown = parts.filter((p) => p.value > 0)
  const folded =
    shown.length > TONES.length
      ? [...shown.slice(0, TONES.length - 1), { key: 'other', label: 'Other', value: shown.slice(TONES.length - 1).reduce((a, p) => a + p.value, 0) }]
      : shown
  const total = folded.reduce((a, p) => a + p.value, 0)
  const pct = (v: number) => (total ? Math.round((v / total) * 100) : 0)
  return (
    <figure className={s.stack} aria-label={label}>
      <div className={s.bar} role="img" aria-label={`${label}: ${folded.map((p) => `${p.label} ${format(p.value)} (${pct(p.value)}%)`).join(', ')}`}>
        {folded.map((p, i) => (
          <span
            key={p.key}
            className={cx(s.seg, s[TONES[i]!], hover === p.key && s.lift)}
            style={{ flexGrow: p.value }}
            title={`${p.label}: ${format(p.value)} (${pct(p.value)}%)`}
            onPointerEnter={() => setHover(p.key)}
            onPointerLeave={() => setHover(null)}
          />
        ))}
      </div>
      <figcaption>
        <ul className={s.legend}>
          {folded.map((p, i) => (
            <li key={p.key} className={cx(s.row, hover === p.key && s.rowOn)} onPointerEnter={() => setHover(p.key)} onPointerLeave={() => setHover(null)}>
              <i className={cx(s.sw, s[TONES[i]!])} aria-hidden="true" />
              <span className={s.name}>{p.label}</span>
              <span className={s.val}>{format(p.value)}</span>
              <span className={s.pct}>{pct(p.value)}%</span>
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  )
}
